<template>
    <div class="map-timebar" 
        :style="css_props"
    >
        <div ref="progress"
            class="progress" 
        >
            <!-- 時間軸中間上方日期時間標示 -->
            <div class="label">
                <div
                    class="text-container"
                >
                    <div 
                        class="circle"
                        :class="{ 'active': !hover_index }" 
                    />
                    <span class="text">
                        {{ date_tag_list[ hover_index || play_index ] }}
                    </span>
                    <span class="text">
                        {{ time_tag_list[ hover_index || play_index ] }}
                    </span>
                </div>
            </div>
            <!-- 時間tag, 點擊區 -->
            <div
                class="center-time"
                :style="{
                    left: `${center_time_left}px`,
                    width: `${center_time_block_width}px`
                }"
            >
                <span>
                    {{ center_time_text }}
                </span>
            </div>
            <div class="block">
                <div v-for="(time, key) in list" 
                    :key="key"
                    class="block-item"
                >
                    <span v-if="extreme_time_tags.includes(time) || time === latest_complete_data_timestamp" 
                        class="block-item__text"
                        :class="{ 'block-item__text--latest-complete': time === latest_complete_data_timestamp }"
                    >
                        <template v-if="!is_mobile_width && extreme_time_tags.includes(time)">
                            {{ date_tag_list[key] }} 
                        </template>
                        <br>
                        {{ time_tag_list[key] }}
                    </span>
                    <span 
                        class="block-item__node"
                        :class="{
                            active: key === play_index,
                            hover: key === hover_index,
                            'block-item__node--latest-complete': time === latest_complete_data_timestamp
                        }"
                        @click="play_index = key"
                        @mouseover="hover_index = key"
                        @mouseout="hover_index = null"
                    />
                </div>
            </div>
        </div>

        <div class="ctrl">
            <QpeTooltip
                tooltip_position="top"
            >
                <template #hovered_item>
                    <v-icon
                        color="white"
                        class="ctrl__icon"
                        @click="rewind_time_unit"
                    >
                        mdi-rewind
                    </v-icon>
                </template>
                <template #tooltip_content>
                    <span>{{ $t('__timeline.rewind') }}</span>
                </template>
            </QpeTooltip>
            <QpeTooltip
                tooltip_position="top"
                v-show="!is_playing"
            >
                <template #hovered_item>
                    <v-icon 
                        color="white"
                        class="ctrl__icon"
                        @click="is_playing = !is_playing"
                    >
                        mdi-play
                    </v-icon>
                </template>
                <template #tooltip_content>
                    <span>{{ $t('__timeline.play') }}</span>
                </template>
            </QpeTooltip>
            <QpeTooltip
                v-show="is_playing"
                tooltip_position="top"
            >
                <template #hovered_item>
                    <v-icon
                        color="white"
                        class="ctrl__icon"
                        @click="is_playing = !is_playing"
                    >
                        mdi-pause
                    </v-icon>
                </template>
                <template #tooltip_content>
                    <span>{{ $t('__timeline.pause') }}</span>
                </template>
            </QpeTooltip>
            <QpeTooltip
                tooltip_position="top"
            >
                <template #hovered_item>
                    <v-icon
                        color="white"
                        class="ctrl__icon"
                        @click="forward_time_unit"
                    >
                        mdi-fast-forward
                    </v-icon>
                </template>
                <template #tooltip_content>
                    <span>{{ $t('__timeline.fast_forward') }}</span>
                </template>
            </QpeTooltip>
            <AutoUpdatePanel v-if="auto_update_btn_activate" />
        </div>
    </div>
</template>

<script>
    import { transform_datetime_to_HHMM } from '@/utils/common/custom_time.js'
    import throttle from 'lodash/throttle';
    import AutoUpdatePanel from '@/components/emap/render_custom_plugin/auto_update_panel.vue'

    import { mapMutations, mapGetters } from 'vuex';

    export default {
        name: "QpeTimeline",

        components: {
            AutoUpdatePanel,
            QpeTooltip: () => import('@/components/tool/qpe_tooltip.vue'),
        },

        props: {
            props_play_index: {
                type: Number,
                require: true,
                default: -1,
            },

            list: {
                type: Array,
                require: true,
                default: () => [],
            },

            duration: {
                type: Number,
                default: () => 1000
            },

            latest_complete_data_timestamp: {
                type: Number,
                require: false,
                default: 0,
            },

            color: {
                type: String,
                default: () => '#fbb03b'
            },

            auto_update_btn_activate: {
                type: Boolean,
                default: true,
            },

            is_show_loading_text: {
                type: Boolean,
                default: false,
            },
        },
        
        data() {
            return {
                day_text: ["(日)", "(一)", "(二)", "(三)", "(四)", "(五)", "(六)"],
                hover_index: null,
                is_playing: false,
                timer: null,

                center_time_left: 0,
                center_time_block_width: 0,
                resize_observer: null,
                loading_times: 0            
            };
        },

        computed: {
            ...mapGetters([
                'is_mobile_width',
            ]),

            css_props() {
                return {
                    "--main-color": this.color,
                };
            },

            play_index: {
                get() {
                    return this.props_play_index
                },
                set(new_index) {
                    if (new_index < 0) {
                        new_index = this.list.length - 1;
                    }

                    if (new_index >= this.list.length) {
                        new_index = 0;
                    }
                    this.$emit('update:props_play_index', new_index)
                },
            },
            
            time_tag_list() {
                return this.list.map((t) => this.format_timestamp(t, "time"));
            },

            date_tag_list() {
                return this.list.map((t) => this.format_timestamp(t, "date"));
            },

            extreme_time_tags() {
                const first_time = this.list[0]
                const last_time = this.list.slice(-1)[0]
                return [first_time, last_time]
            },

            center_timestamp() {
                return (this.extreme_time_tags[0] + this.extreme_time_tags[1])/2
                
            },

            center_time_text() {
                return transform_datetime_to_HHMM(new Date(this.center_timestamp))
            },
        },

        watch: {
            is_playing(status) {
                status ? this.play() : this.pause()
            },
            
            play_index(idx) {
                const timestamp = this.list[idx];
                this.set_current_timestamp(timestamp)
                this.$emit('trigger-change-time', idx, timestamp)
            },
        },
    
        mounted() {
            this.start_resize_observer()
            document.addEventListener('keydown', this.throttle_call_rerender_function)
        },

        beforeDestroy() {
            this.resize_observer.unobserve(this.$refs.progress)
            document.removeEventListener('keydown', this.throttle_call_rerender_function) 
            this.pause()
        },
    
        methods: {
            ...mapMutations('emap', [
                'set_current_timestamp'
            ]),
            
            change_idx(key, type = "idx") {
                // 預設使用idx來切換, 可切換成timestamp
                const length = this.list.length;
                const last_idx = length - 1;

                let play_index = last_idx; //預設播放最後一張
                if (type === "idx" && key < length) {
                    //指定idx
                    play_index = key;
                } else if (type === "timestamp" && this.list.indexOf(key) >= 0) {
                    //指定timestamp
                    play_index = this.list.indexOf(key);
                }

                this.play_index = play_index;
            },
            play() {
                this.timer = setInterval(() => {
                    this.forward_time_unit()
                }, this.duration);
            },
            pause() {
                clearInterval(this.timer);
            },
            format_timestamp(timestamp, format) {
                const d = new Date(parseInt(timestamp));
                const obj = {
                    yyyy: d.getFullYear(),
                    mm: this.format_add_zero(d.getMonth() + 1),
                    dd: this.format_add_zero(d.getDate()),
                    HH: this.format_add_zero(d.getHours()),
                    MM: this.format_add_zero(d.getMinutes()),
                    DAY: this.day_text[d.getDay()],
                };

                const date = obj["yyyy"] + "-" + obj["mm"] + "-" + obj["dd"];
                const day = obj["DAY"];
                const time = obj["HH"] + ":" + obj["MM"];

                if (format === "date") {
                    return date;
                } else if (format === "day") {
                    return day;
                } else if (format === "time") {
                    return time;
                } else {
                    return date + day + " " + time;
                }
            },
            
            format_add_zero(data) {
                let str = String(data);
                return str.length < 2 ? "0" + str : str;
            },

            rewind_time_unit() {
                this.play_index--;
            },

            forward_time_unit() {
                if (this.loading_times < 2 && this.is_show_loading_text) return this.loading_times ++
                this.loading_times = 0
                this.play_index++;
            },

            start_resize_observer() {
                this.$nextTick(() => {
                    this.resize_observer = new ResizeObserver(() => {
                        const dom_list_of_time = document.querySelectorAll('.block-item')
                        if (dom_list_of_time.length === 0) return
                        this.calc_center_time_left(dom_list_of_time)
                        this.calc_center_time_width(dom_list_of_time)
                    })
                    
                    this.resize_observer.observe(this.$refs.progress)
                })
            },

            calc_center_time_left(dom_list_of_time) {
                const _get_offset_left = (dom_list, index) => {
                    const body_rect = document.body.getBoundingClientRect()
                    const dom_rect = dom_list[index].getBoundingClientRect()
                    return dom_rect.left - body_rect.left
                }

                const time_that_next_center_time_index = this.list.findIndex(time => time >= this.center_timestamp)
                const time_that_prev_center_time = this.list[time_that_next_center_time_index - 1]
                const time_that_next_center_time = this.list[time_that_next_center_time_index]

                const offset_left_that_first_tiem = _get_offset_left(dom_list_of_time, 0)
                const offset_left_that_prev_center_time = _get_offset_left(dom_list_of_time, time_that_next_center_time_index - 1)
                const offset_left_that_next_center_time = _get_offset_left(dom_list_of_time, time_that_next_center_time_index)
                
                const slope = (offset_left_that_next_center_time - offset_left_that_prev_center_time) / (time_that_next_center_time - time_that_prev_center_time)
                this.center_time_left = (this.center_timestamp - time_that_prev_center_time)*slope + offset_left_that_prev_center_time - offset_left_that_first_tiem
            },

            calc_center_time_width(dom_list_of_time) {
                this.center_time_block_width = dom_list_of_time[0].offsetWidth
            },

            throttle_call_rerender_function: throttle(function(e) { // 不能 arrow function
                this.listen_keyboard_event(e)
            }, 100),

            listen_keyboard_event({ code }) {
                if (['PageUp', 'KeyA'].includes(code)) {
                    return this.rewind_time_unit()
                }
                if (['PageDown', 'KeyD'].includes(code)) {
                    return this.forward_time_unit()
                }
                if (code === 'Space') {
                    this.is_playing = !this.is_playing
                }
            },
        },
    };

</script>

<style lang="scss" scoped>
    @import '../../../../css/mixin';

    $latest-complete-color: #ffbb03;

    @mixin time-node-gray-background {
        content: '';
        position: absolute;
        bottom: .75rem;
        right: 0;
        left: 0;
        margin: auto;
        width: .5rem;
        height: 1rem;
        background-color: #595e63;
        border-radius: 3px;
        opacity: 1;
        transition: opacity .3s;
    }

    .map-timebar {
        display: flex;
        position: absolute;
        width: 100%;
        height: 4em;
        font-size: 1em;

        @include mobile {
            flex-direction: column;
            margin-bottom: 20px;
        }

        /* 控制按鈕 */
        .ctrl {
            display: flex;
            flex: 0 0 auto;
            margin: auto .25rem;

            @include mobile {
                justify-content: center;
            }

            &__icon {
                margin: auto .5rem;
                width: 1.5rem;
            }
        }

        /* 進度條 & 時間tag */
        .progress {
            position: relative;
            flex: 1 1 auto;
            margin: 0 8px 10px;

            @include mobile {
                margin: 0 8px;
            }

            .label {
                position: absolute;
                bottom: 3.5rem;
                right: 0;
                left: 0;
                display: flex;
                justify-content: center;
                text-align: center;
                pointer-events: none;

                .text-container {
                    padding: .3rem;
                    color: #fff;
                    background-color: rgba($color: #fff, $alpha: .16);
                    border-radius: 12px;
                }

                .circle {
                    display: inline-flex;
                    width: 1rem;
                    height: 1rem;

                    &::before {
                        content: ' ';
                        position: relative;
                        top: .1rem;
                        width: 1rem;
                        height: 1rem;
                        background-color: #fff;
                        border-radius: .5rem;
                    }

                    &.active::before {
                        background-color: var(--aqua-active);
                    }
                }
            }

            /* 時間tag, 點擊區 */
            .block {
                display: flex;
                height: 100%;
            }

            .block-item {
                position: relative;
                display: flex;
                flex: 1 1 auto;
                justify-content: center;
                text-align: center;

                &__text {
                    position: absolute;
                    top: -1.5em;
                    width: max-content;
                    height: 3em;
                    font-size: .9em;
                    color: #fff;
                    opacity: 1;
                    transition: .5s;
                    pointer-events: none;

                    @include mobile {
                        top: -3em;
                    }

                    &--latest-complete {
                        color: $latest-complete-color;
                    }
                }

                /* 時間tag */
                &:first-child > .block-item__text {
                    left: 0;
                    text-align: left;
                }

                &:last-child > .block-item__text {
                    right: 0;
                    text-align: right;
                }

                /* 時間tag 節點 */
                @mixin time-node-circle {
                    content: '';
                    position: absolute;
                    bottom: .75rem;
                    right: 0;
                    left: 0;
                    margin: auto;
                    width: 1rem;
                    height: 1rem;
                    border-radius: .5rem;
                    opacity: 1;
                    transition: opacity .3s;
                }

                // 灰色的時間節點背景
                &:first-child,
                &:last-child {
                    > .block-item__node::before {
                        @include time-node-gray-background;
                    }
                }

                &__node {
                    cursor: pointer;
                }

                &__node::after {
                    content: '';
                    position: absolute;
                    bottom: 1rem;
                    right: 0;
                    left: 0;
                    margin: auto;
                    width: .125rem;
                    height: .5rem;
                    background-color: #e8ecf7;
                    border-radius: 1rem;
                    opacity: 1;
                    transition: opacity .3s;
                }

                &__node:hover::after {
                    @include time-node-circle;

                    background-color: #fff;
                }

                &__node--latest-complete::after {
                    background-color: $latest-complete-color;
                }

                &__node.active::after,
                &__node.active:hover::after {
                    @include time-node-circle;

                    background-color: var(--aqua-active);
                }
            }

            .center-time {
                position: absolute;
                bottom: -1px;
                display: flex;
                flex: 1 1 auto;
                justify-content: center;
                text-align: center;

                span {
                    position: absolute;
                    top: -3.5rem;
                    padding: 0 10px;
                    width: max-content;
                    height: 3em;
                    font-size: .9em;
                    color: #fff;
                    transition: .5s;

                    @include mobile {
                        top: -3.75rem;
                    }
                }

                &::before {
                    @include time-node-gray-background;
                }
            }
        }

        .progress::before {
            content: '';
            position: absolute;
            bottom: 1rem;
            width: 100%;
            height: .5rem;
            background-color: #595e63;
            border-radius: 4px;
        }
    }

    .text+.text {
        margin-left: 10px;
    }

    // 下次部屬正式機確認無誤後就可以刪除
    // .iOS .map-timebar {
    //     /* 進度條 & 時間tag */
    //     .progress {
    //         .block-item {
    //             .block-item__node {
    //                 &::before {
    //                     bottom: -1.75rem;
    //                 }

    //                 &::after {
    //                     bottom: -1.5rem;
    //                 }

    //                 &.active::after {
    //                     bottom: -1.75rem;
    //                 }
    //             }
    //         }

    //         .center-time {
    //             span {
    //                 width: initial;
    //                 white-space: nowrap;
    //             }
    //         }
    //     }
    // }

    // .Safari .map-timebar {
    //     .progress {
    //         .center-time {
    //             span {
    //                 width: initial;
    //                 white-space: nowrap;
    //             }
    //         }
    //     }
    // }

</style>
